import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CircularProgress from "@mui/material/CircularProgress";
import {
  addPartCatalogAttribute,
  addPartCatalogRestriction,
  deletePartCatalogAttribute,
  deletePartCatalogRestriction,
  fetchAllActiveAttributes,
  fetchAllActiveRestrictions,
  fetchPartAttributesByItemId,
  fetchPartDetailsByItemId,
  fetchPartRestrictionsByItemId,
  isRestrictionsNoDataError,
  fetchSubstitutePartsByItemId,
  updatePartCatalogAttribute,
  updatePartCatalogRestrictionIncludeExclude,
} from "../../graphql/partsCatalogQueries";
import {
  cellText,
  availabilityTooltipForCode,
} from "../../utils/partCatalogFormatters";
import { hasPartsCatalogUpdateAccessFromStorage } from "../../utils/authAccess";
import "../../pages/partsCatalogPartDetails/partDetails.css";
import "./partDetailsModal.css";

function partDetailTitleSuffix(row) {
  if (!row) return "";
  const d =
    row.div != null && String(row.div).trim() !== ""
      ? String(row.div).trim()
      : "";
  const p =
    row.pls != null && String(row.pls).trim() !== ""
      ? String(row.pls).trim()
      : "";
  const n =
    row.partNumber != null && String(row.partNumber).trim() !== ""
      ? String(row.partNumber).trim()
      : "";
  if (d && p && n) return `${d}-${p}-${n}`;
  if (n) return n;
  const id =
    row.itemId != null && String(row.itemId).trim() !== ""
      ? String(row.itemId).trim()
      : "";
  return id || "—";
}

/** Normalize availability for tooltip + display (scalar code or `{ code }`). */
function normalizeAvailabilityShape(avail) {
  if (avail == null) return { code: "" };
  if (typeof avail === "object" && avail !== null && !Array.isArray(avail)) {
    const c = avail.code ?? avail.availability ?? "";
    return { code: String(c).trim() };
  }
  return { code: String(avail).trim() };
}

/** Build detail view model from search grid row when API detail is missing. */
function partDetailFromSnapshot(snap) {
  if (!snap) return null;
  return {
    itemId: snap.itemId ?? "",
    div: snap.div ?? "",
    pls: snap.pls ?? "",
    partNumber: snap.partNumber ?? "",
    description: snap.partName ?? "",
    partRank: "",
    addedDate: snap.partAddDate ?? "",
    availability: normalizeAvailabilityShape(snap.availability),
    partPrice: "",
    son: "",
    imageUrl: "",
    divisionDescription: "",
    accessoryFlag: "",
    upc: "",
    partSourceCode: "",
    routeCode: "",
    attributes: snap.attributes,
    restrictions: snap.restrictions,
    partName: snap.partName ?? "",
    partAddDate: snap.partAddDate ?? "",
  };
}

/** Merge `getPartDetailsByItemId` with snapshot attributes/restrictions for accordions. */
function mergePartDetailFromApi(api, snap) {
  const fallback = partDetailFromSnapshot(snap);
  if (!api || typeof api !== "object") return fallback;
  const base = fallback ?? {};
  return {
    ...base,
    ...api,
    availability: normalizeAvailabilityShape(
      api.availability !== undefined && api.availability !== null
        ? api.availability
        : base.availability
    ),
    attributes: snap?.attributes ?? base.attributes,
    restrictions: snap?.restrictions ?? base.restrictions,
    description: api.description ?? base.description ?? "",
    partName: api.description ?? snap?.partName ?? base.partName ?? "",
    partAddDate: api.addedDate ?? snap?.partAddDate ?? base.partAddDate ?? "",
  };
}

/** Catalog REST `partId`: DIV + PLS + part number (e.g. 0022364WB28K154). */
function buildPartCatalogPartId(p) {
  if (!p) return "";
  return `${String(p.div ?? "").trim()}${String(p.pls ?? "").trim()}${String(p.partNumber ?? "").trim()}`;
}

/** Format price like `$54.98` when numeric; pass through if already has `$`. */
function formatPartPrice(v) {
  if (v == null) return "-";
  const raw = String(v).trim();
  if (raw === "") return "-";
  if (raw.startsWith("$")) return raw;
  const n = Number(String(raw).replace(/,/g, ""));
  if (!Number.isNaN(n) && Number.isFinite(n)) {
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(n);
    } catch {
      return `$${n}`;
    }
  }
  return raw;
}

/** Display accessory / boolean-like flags from API. */
function formatAccessoryFlag(v) {
  if (v === true || v === 1) return "Yes";
  if (v === false || v === 0) return "No";
  const s = String(v).trim().toLowerCase();
  if (s === "true" || s === "y" || s === "yes") return "Yes";
  if (s === "false" || s === "n" || s === "no") return "No";
  return cellText(v);
}

/** Valid Include/exclude values: `I` or `E` (case-insensitive). */
function normalizeRestrictionIncludeExclude(v) {
  const t = String(v ?? "").trim().toUpperCase();
  if (t === "I" || t === "E") return t;
  return null;
}

/** `restrictionIncludeExclude` display: PART/E → blank; DIV-PLS → checked checkbox. */
function restrictionDivplsIncludeExcludeKind(raw) {
  const t = String(raw ?? "").trim().toUpperCase();
  if (t === "PART" || t === "E") return "part";
  if (t === "DIV-PLS" || t === "DIVPLS") return "divpls";
  return "other";
}

/** `includeExcludeFlag` query param when DIVPLS include/exclude is unchecked (always `E`). */
const DIVPLS_UNCHECK_API_INCLUDE_EXCLUDE_FLAG = "E";

function RestrictionDivplsIncludeExcludeCell({
  value,
  disabled = false,
  saving = false,
  readOnly = false,
  onToggle,
}) {
  const kind = restrictionDivplsIncludeExcludeKind(value);
  if (kind === "part") {
    return <span className="pscDivplsIncludeExcludeBlank" aria-hidden="true" />;
  }
  if (kind === "divpls") {
    return (
      <div className="pscDivplsIncludeExcludeEditWrap">
        <input
          type="checkbox"
          className="pscDivplsIncludeExcludeCheckbox"
          checked
          disabled={readOnly || disabled || saving}
          readOnly={readOnly}
          onChange={(e) => {
            if (readOnly) return;
            if (!e.target.checked) onToggle?.(false);
          }}
          aria-label="DIVPLS include/exclude"
          aria-busy={saving ? "true" : "false"}
          title="DIV-PLS"
        />
        {saving ? (
          <CircularProgress size={16} thickness={4} aria-label="Saving" />
        ) : null}
      </div>
    );
  }
  return <span className="pscDivplsIncludeExcludeBlank" aria-hidden="true" />;
}

const EMPTY_RESTRICTION_ADD_DRAFT = {
  restrictionId: "",
  includeExcludeFlag: "I",
};

const EMPTY_ATTRIBUTE_ADD_DRAFT = {
  attributeId: "",
  value: "",
  uom: "",
};

/** Map `getPartRestrictionsByItemId` rows for the restrictions table. */
function mapPartRestrictionsFromApi(apiRows) {
  return (Array.isArray(apiRows) ? apiRows : []).map((r) => ({
    restrictionId: r.restrictionId ?? "",
    restrictionName: r.restrictionName ?? "",
    restrictionIncludeExclude: r.restrictionIncludeExclude ?? "",
  }));
}

function mapPartRestrictionsWithSearchFallback(apiRows, searchRests) {
  let rows = mapPartRestrictionsFromApi(apiRows);
  if (
    rows.length === 0 &&
    Array.isArray(searchRests) &&
    searchRests.length > 0
  ) {
    rows = searchRests.map((r) => ({
      restrictionId: r.restrictionId ?? "",
      restrictionName: r.name ?? "",
      restrictionIncludeExclude: r.includeExcludeFlag ?? "",
    }));
  }
  return rows;
}

/** Map attribute rows from API + search snapshot names into table rows. */
function mapPartAttributesForTable(apiRows, searchAttrs) {
  const nameById = new Map();
  (Array.isArray(searchAttrs) ? searchAttrs : []).forEach((a) => {
    const k = String(a.attributeId ?? "").trim();
    if (k) nameById.set(k, a.name ?? "");
  });
  return (Array.isArray(apiRows) ? apiRows : []).map((r) => {
    const id = String(r.attributeId ?? "").trim();
    const apiName =
      r.attributeName != null ? String(r.attributeName).trim() : "";
    const searchName = nameById.get(id) ?? "";
    return {
      attributeId: r.attributeId ?? "",
      name: apiName || searchName,
      value: r.value ?? "",
      uom: r.uom ?? "",
    };
  });
}

/**
 * Modal part details overlay (opens on top of Part Search).
 * Attributes, restrictions, and substitution stay collapsed until expanded.
 */
export default function PartDetailsModal({ open, snapshotRow, onClose }) {
  const snapshotRef = useRef(snapshotRow);
  snapshotRef.current = snapshotRow;

  const itemId =
    snapshotRow?.itemId != null && String(snapshotRow.itemId).trim() !== ""
      ? String(snapshotRow.itemId).trim()
      : "";

  const [part, setPart] = useState(snapshotRow ?? null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [softWarning, setSoftWarning] = useState(null);

  const [attrsExpanded, setAttrsExpanded] = useState(false);
  const [restsExpanded, setRestsExpanded] = useState(false);
  const [subsExpanded, setSubsExpanded] = useState(false);
  const [attrsDetailRows, setAttrsDetailRows] = useState(null);
  const [attrsDetailLoading, setAttrsDetailLoading] = useState(false);
  const [attrsDetailError, setAttrsDetailError] = useState(null);
  const [attrDeletingRowIndex, setAttrDeletingRowIndex] = useState(null);
  const [attributeDeleteError, setAttributeDeleteError] = useState(null);
  const [attributeUpdateSuccessMessage, setAttributeUpdateSuccessMessage] =
    useState(null);
  /** `{ index, field: 'value' | 'uom', text }` while editing one attribute cell. */
  const [attrValueUomEdit, setAttrValueUomEdit] = useState(null);
  const [attrValueUomError, setAttrValueUomError] = useState(null);
  const [attrValueUomSaving, setAttrValueUomSaving] = useState(null);
  const attrEditInputRef = useRef(null);
  const skipAttrValueUomBlurCommitRef = useRef(false);

  const [restsDetailRows, setRestsDetailRows] = useState(null);
  const [restsDetailLoading, setRestsDetailLoading] = useState(false);
  const [restsDetailError, setRestsDetailError] = useState(null);
  const [restDeletingRowIndex, setRestDeletingRowIndex] = useState(null);
  const [restrictionDeleteError, setRestrictionDeleteError] = useState(null);
  const [restrictionSuccessMessage, setRestrictionSuccessMessage] =
    useState(null);
  const [restrictionAddOpen, setRestrictionAddOpen] = useState(false);
  const [restrictionAddDraft, setRestrictionAddDraft] = useState(() => ({
    ...EMPTY_RESTRICTION_ADD_DRAFT,
  }));
  const [restrictionAddSubmitting, setRestrictionAddSubmitting] =
    useState(false);
  const [restrictionAddError, setRestrictionAddError] = useState(null);
  const [divplsIncludeExcludeSavingIndex, setDivplsIncludeExcludeSavingIndex] =
    useState(null);
  const divplsIncludeExcludeCommitInFlightRef = useRef(false);
  const [activeRestrictions, setActiveRestrictions] = useState([]);
  const [activeRestrictionsLoading, setActiveRestrictionsLoading] =
    useState(false);
  const [activeRestrictionsError, setActiveRestrictionsError] =
    useState(null);
  const [activeRestrictionsFetchAttempted, setActiveRestrictionsFetchAttempted] =
    useState(false);
  const activeRestrictionsInFlightRef = useRef(false);
  const activeRestrictionsLoadedOkRef = useRef(false);
  const [attributeAddOpen, setAttributeAddOpen] = useState(false);
  const [attributeAddDraft, setAttributeAddDraft] = useState(() => ({
    ...EMPTY_ATTRIBUTE_ADD_DRAFT,
  }));
  const [attributeAddSubmitting, setAttributeAddSubmitting] = useState(false);
  const [attributeAddError, setAttributeAddError] = useState(null);
  const [activeAttributes, setActiveAttributes] = useState([]);
  const [activeAttributesLoading, setActiveAttributesLoading] =
    useState(false);
  const [activeAttributesError, setActiveAttributesError] = useState(null);
  const [activeAttributesFetchAttempted, setActiveAttributesFetchAttempted] =
    useState(false);
  const activeAttributesInFlightRef = useRef(false);
  const activeAttributesLoadedOkRef = useRef(false);

  const [subsDetailRows, setSubsDetailRows] = useState(null);
  const [subsDetailLoading, setSubsDetailLoading] = useState(false);
  const [subsDetailError, setSubsDetailError] = useState(null);

  const [enlargedImageUrl, setEnlargedImageUrl] = useState(null);

  const canEditPartDetails = useMemo(
    () => hasPartsCatalogUpdateAccessFromStorage(),
    []
  );

  useEffect(() => {
    if (!open) setEnlargedImageUrl(null);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (enlargedImageUrl) {
        setEnlargedImageUrl(null);
        return;
      }
      if (attributeAddOpen) {
        if (attributeAddSubmitting) return;
        setAttributeAddOpen(false);
        setAttributeAddDraft({ ...EMPTY_ATTRIBUTE_ADD_DRAFT });
        setAttributeAddError(null);
        return;
      }
      if (attrValueUomEdit) {
        if (attrValueUomSaving != null) return;
        skipAttrValueUomBlurCommitRef.current = true;
        setAttrValueUomEdit(null);
        setAttrValueUomError(null);
        return;
      }
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    open,
    onClose,
    enlargedImageUrl,
    attributeAddOpen,
    attributeAddSubmitting,
    attrValueUomEdit,
    attrValueUomSaving,
  ]);

  useEffect(() => {
    if (attrValueUomEdit == null) return undefined;
    const id = requestAnimationFrame(() => {
      attrEditInputRef.current?.focus();
      attrEditInputRef.current?.select?.();
    });
    return () => cancelAnimationFrame(id);
  }, [attrValueUomEdit?.index, attrValueUomEdit?.field]);

  useEffect(() => {
    if (attrValueUomEdit == null || attrValueUomError == null)
      return undefined;
    const id = requestAnimationFrame(() => {
      attrEditInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [
    attrValueUomEdit?.index,
    attrValueUomEdit?.field,
    attrValueUomError,
  ]);

  useEffect(() => {
    if (!restrictionSuccessMessage) return undefined;
    const id = setTimeout(() => setRestrictionSuccessMessage(null), 5000);
    return () => clearTimeout(id);
  }, [restrictionSuccessMessage]);

  useEffect(() => {
    if (!attributeUpdateSuccessMessage) return undefined;
    const id = setTimeout(() => setAttributeUpdateSuccessMessage(null), 5000);
    return () => clearTimeout(id);
  }, [attributeUpdateSuccessMessage]);

  useEffect(() => {
    if (!open) return undefined;

    setPart(snapshotRow ?? null);
    setError(null);
    setSoftWarning(null);

    if (!itemId) {
      setLoading(false);
      setError("Missing Item ID.");
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const detail = await fetchPartDetailsByItemId(itemId);
        if (cancelled) return;
        const snap = snapshotRef.current;
        if (detail && typeof detail === "object") {
          setPart(mergePartDetailFromApi(detail, snap));
        } else if (snap) {
          setPart(partDetailFromSnapshot(snap));
          setSoftWarning(
            "Could not load part details from the server. Showing the data from your last search."
          );
        } else {
          setPart(null);
          setError("No part was found for this Item ID.");
        }
      } catch (e) {
        if (cancelled) return;
        const snap = snapshotRef.current;
        if (snap) {
          setPart(partDetailFromSnapshot(snap));
          setSoftWarning(
            e?.message
              ? `Could not load part details (${e.message}). Showing data from your last search.`
              : "Could not load part details. Showing data from your last search."
          );
        } else {
          setPart(null);
          setError(e?.message || "Failed to load part details.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, itemId, snapshotRow]);

  useEffect(() => {
    if (!open) {
      setAttrsExpanded(false);
      setRestsExpanded(false);
      setSubsExpanded(false);
      setAttrsDetailRows(null);
      setAttrsDetailError(null);
      setAttrsDetailLoading(false);
      setAttrDeletingRowIndex(null);
      setAttributeDeleteError(null);
      setAttributeUpdateSuccessMessage(null);
      setAttrValueUomEdit(null);
      setAttrValueUomError(null);
      setAttrValueUomSaving(null);
      setRestsDetailRows(null);
      setRestsDetailError(null);
      setRestsDetailLoading(false);
      setSubsDetailRows(null);
      setSubsDetailError(null);
      setSubsDetailLoading(false);
      setRestDeletingRowIndex(null);
      setRestrictionDeleteError(null);
      setRestrictionSuccessMessage(null);
      setRestrictionAddOpen(false);
      setRestrictionAddDraft({ ...EMPTY_RESTRICTION_ADD_DRAFT });
      setRestrictionAddSubmitting(false);
      setRestrictionAddError(null);
      setDivplsIncludeExcludeSavingIndex(null);
      setAttributeAddOpen(false);
      setAttributeAddDraft({ ...EMPTY_ATTRIBUTE_ADD_DRAFT });
      setAttributeAddSubmitting(false);
      setAttributeAddError(null);
      return;
    }
    setAttrsExpanded(false);
    setRestsExpanded(false);
    setSubsExpanded(false);
    setAttrsDetailRows(null);
    setAttrsDetailError(null);
    setAttrsDetailLoading(false);
    setAttrDeletingRowIndex(null);
    setAttributeDeleteError(null);
    setAttributeUpdateSuccessMessage(null);
    setAttrValueUomEdit(null);
    setAttrValueUomError(null);
    setAttrValueUomSaving(null);
    setRestsDetailRows(null);
    setRestsDetailError(null);
    setRestsDetailLoading(false);
    setSubsDetailRows(null);
    setSubsDetailError(null);
    setSubsDetailLoading(false);
    setRestDeletingRowIndex(null);
    setRestrictionDeleteError(null);
    setRestrictionSuccessMessage(null);
    setRestrictionAddOpen(false);
    setRestrictionAddDraft({ ...EMPTY_RESTRICTION_ADD_DRAFT });
    setRestrictionAddSubmitting(false);
    setRestrictionAddError(null);
    setDivplsIncludeExcludeSavingIndex(null);
    setAttributeAddOpen(false);
    setAttributeAddDraft({ ...EMPTY_ATTRIBUTE_ADD_DRAFT });
    setAttributeAddSubmitting(false);
    setAttributeAddError(null);
  }, [open, itemId]);

  const handleAttrsAccordionToggle = useCallback(async () => {
    if (attrsExpanded) {
      setAttrsExpanded(false);
      setAttributeDeleteError(null);
      setAttributeUpdateSuccessMessage(null);
      setAttrDeletingRowIndex(null);
      setAttrValueUomEdit(null);
      setAttrValueUomError(null);
      setAttrValueUomSaving(null);
      setAttributeAddOpen(false);
      setAttributeAddDraft({ ...EMPTY_ATTRIBUTE_ADD_DRAFT });
      setAttributeAddSubmitting(false);
      setAttributeAddError(null);
      return;
    }
    setAttrsExpanded(true);
    if (attrsDetailRows !== null || !itemId) return;

    setAttrsDetailLoading(true);
    setAttrsDetailError(null);
    setAttributeDeleteError(null);
    setAttributeUpdateSuccessMessage(null);
    try {
      const apiList = await fetchPartAttributesByItemId(itemId);
      const searchAttrs = part?.attributes;
      let rows = mapPartAttributesForTable(apiList, searchAttrs);
      if (
        rows.length === 0 &&
        Array.isArray(searchAttrs) &&
        searchAttrs.length > 0
      ) {
        rows = searchAttrs.map((a) => ({
          attributeId: a.attributeId ?? "",
          name: a.name ?? "",
          value: a.value ?? "",
          uom: a.uom ?? "",
        }));
      }
      setAttrsDetailRows(rows);
    } catch (e) {
      console.error(e);
      const fallback = Array.isArray(part?.attributes) ? part.attributes : [];
      const rows = fallback.map((a) => ({
        attributeId: a.attributeId ?? "",
        name: a.name ?? "",
        value: a.value ?? "",
        uom: a.uom ?? "",
      }));
      setAttrsDetailRows(rows);
      setAttrsDetailError(
        rows.length === 0 ? e?.message || "Failed to load attributes" : null
      );
    } finally {
      setAttrsDetailLoading(false);
    }
  }, [attrsExpanded, attrsDetailRows, itemId, part]);

  const removeAttributeRow = useCallback(
    async (index) => {
      if (!canEditPartDetails) return;
      const rows = attrsDetailRows;
      if (!Array.isArray(rows) || index < 0 || index >= rows.length) return;
      const row = rows[index];
      const attributeId = String(row.attributeId ?? "").trim();
      if (!attributeId) {
        window.alert("Missing attribute ID.");
        return;
      }
      if (!window.confirm("Delete this attribute from the server?")) return;

      const pic = part || snapshotRow;
      const partId = buildPartCatalogPartId(pic);
      if (!partId) {
        window.alert(
          "Cannot build partId (DIV + PLS + part number). Check part data."
        );
        return;
      }

      const username =
        (typeof localStorage !== "undefined" &&
          localStorage.getItem("username")) ||
        "DBEditorWeb";

      setAttributeDeleteError(null);
      setAttributeUpdateSuccessMessage(null);
      setAttrValueUomEdit((cur) =>
        cur && cur.index === index ? null : cur
      );
      setAttrValueUomError(null);
      setAttrDeletingRowIndex(index);
      try {
        await deletePartCatalogAttribute({
          username,
          partId,
          attributeId,
        });
        setAttrsDetailRows((prev) =>
          Array.isArray(prev) ? prev.filter((_, idx) => idx !== index) : prev
        );
        setAttributeUpdateSuccessMessage("Attribute deleted successfully.");
        setAttrValueUomEdit((cur) => {
          if (!cur) return null;
          if (cur.index === index) return null;
          if (cur.index > index) return { ...cur, index: cur.index - 1 };
          return cur;
        });
      } catch (e) {
        console.error(e);
        const data = e?.response?.data;
        const msg =
          (typeof data === "string" && data) ||
          data?.message ||
          e?.message ||
          "Failed to delete attribute.";
        setAttributeDeleteError(msg);
      } finally {
        setAttrDeletingRowIndex(null);
      }
    },
    [canEditPartDetails, part, snapshotRow, attrsDetailRows]
  );

  const commitAttrValueUom = useCallback(
    async (index, field, text, fromBlur = false, rowCtx = null) => {
      if (!canEditPartDetails) return;
      if (fromBlur && skipAttrValueUomBlurCommitRef.current) {
        skipAttrValueUomBlurCommitRef.current = false;
        return;
      }
      const attributeId = String(rowCtx?.attributeId ?? "").trim();
      if (!attributeId) {
        setAttrValueUomError("Missing attribute ID.");
        return;
      }

      const vOut =
        field === "value"
          ? String(text ?? "")
          : String(rowCtx?.value ?? "");
      const uOut =
        field === "uom"
          ? String(text ?? "")
          : String(rowCtx?.uom ?? "");
      const prevV = String(rowCtx?.value ?? "");
      const prevU = String(rowCtx?.uom ?? "");
      if (vOut === prevV && uOut === prevU) {
        setAttrValueUomError(null);
        setAttrValueUomEdit(null);
        return;
      }

      const pic = part || snapshotRow;
      const partId = buildPartCatalogPartId(pic);
      if (!partId) {
        setAttrValueUomError(
          "Cannot build partId (DIV + PLS + part number). Check part data."
        );
        return;
      }

      const username =
        (typeof localStorage !== "undefined" &&
          localStorage.getItem("username")) ||
        "DBEditorWeb";

      setAttrValueUomError(null);
      setAttributeDeleteError(null);
      setAttributeUpdateSuccessMessage(null);
      setAttrValueUomSaving({ index, field });
      try {
        await updatePartCatalogAttribute({
          username,
          partId,
          attributeId,
          value: vOut,
          uom: uOut,
        });
        setAttrsDetailRows((prev) => {
          if (!Array.isArray(prev)) return prev;
          return prev.map((row, idx) =>
            idx === index ? { ...row, value: vOut, uom: uOut } : row
          );
        });
        setAttrValueUomEdit(null);
        setAttributeUpdateSuccessMessage("Attribute updated successfully.");
      } catch (e) {
        console.error(e);
        setAttributeUpdateSuccessMessage(null);
        const data = e?.response?.data;
        const msg =
          (typeof data === "string" && data) ||
          data?.message ||
          e?.message ||
          "Failed to update attribute.";
        setAttrValueUomError(msg);
      } finally {
        setAttrValueUomSaving(null);
      }
    },
    [canEditPartDetails, part, snapshotRow]
  );

  /** Reload restrictions from `getPartRestrictionsByItemId` after add/update/delete. */
  const refreshRestsDetailRows = useCallback(async () => {
    if (!itemId) return false;
    try {
      const apiRows = await fetchPartRestrictionsByItemId(itemId);
      setRestsDetailRows(mapPartRestrictionsFromApi(apiRows));
      setRestsDetailError(null);
      return true;
    } catch (e) {
      console.error(e);
      if (isRestrictionsNoDataError(e)) {
        setRestsDetailRows([]);
        setRestsDetailError(null);
      } else {
        setRestsDetailError(e?.message || "Failed to refresh restrictions.");
      }
      return false;
    }
  }, [itemId]);

  const handleRestsAccordionToggle = useCallback(async () => {
    if (restsExpanded) {
      setRestsExpanded(false);
      setDivplsIncludeExcludeSavingIndex(null);
      setRestrictionSuccessMessage(null);
      setRestrictionAddOpen(false);
      setRestrictionAddDraft({ ...EMPTY_RESTRICTION_ADD_DRAFT });
      setRestrictionAddSubmitting(false);
      setRestrictionAddError(null);
      return;
    }
    setRestsExpanded(true);
    if (restsDetailRows !== null || !itemId) return;

    setRestsDetailLoading(true);
    setRestsDetailError(null);
    setRestrictionDeleteError(null);
    try {
      const apiRows = await fetchPartRestrictionsByItemId(itemId);
      setRestsDetailRows(mapPartRestrictionsFromApi(apiRows));
    } catch (e) {
      console.error(e);
      if (isRestrictionsNoDataError(e)) {
        setRestsDetailRows([]);
        setRestsDetailError(null);
      } else {
        const rows = mapPartRestrictionsWithSearchFallback(
          [],
          part?.restrictions
        );
        setRestsDetailRows(rows);
        setRestsDetailError(
          rows.length === 0
            ? e?.message || "Failed to load restrictions"
            : null
        );
      }
    } finally {
      setRestsDetailLoading(false);
    }
  }, [restsExpanded, restsDetailRows, itemId, part]);

  /** Same GraphQL as Part Search: load `allActiveRestrictions` once, on first focus of the dropdown. */
  const loadActiveRestrictionsForAdd = useCallback(async () => {
    if (activeRestrictionsInFlightRef.current) return;
    if (activeRestrictionsLoadedOkRef.current) return;
    activeRestrictionsInFlightRef.current = true;
    setActiveRestrictionsLoading(true);
    setActiveRestrictionsError(null);
    try {
      const list = await fetchAllActiveRestrictions();
      setActiveRestrictions(list);
      activeRestrictionsLoadedOkRef.current = true;
    } catch (e) {
      setActiveRestrictions([]);
      setActiveRestrictionsError(e?.message || "Failed to load restrictions");
      console.error(e);
    } finally {
      activeRestrictionsInFlightRef.current = false;
      setActiveRestrictionsLoading(false);
      setActiveRestrictionsFetchAttempted(true);
    }
  }, []);

  const closeAttributeAddForm = useCallback(() => {
    setAttributeAddOpen(false);
    setAttributeAddDraft({ ...EMPTY_ATTRIBUTE_ADD_DRAFT });
    setAttributeAddError(null);
  }, []);

  /** Same GraphQL as Part Search: load `allActiveAttributes` once, on first focus of the dropdown. */
  const loadActiveAttributesForAdd = useCallback(async () => {
    if (activeAttributesInFlightRef.current) return;
    if (activeAttributesLoadedOkRef.current) return;
    activeAttributesInFlightRef.current = true;
    setActiveAttributesLoading(true);
    setActiveAttributesError(null);
    try {
      const list = await fetchAllActiveAttributes();
      setActiveAttributes(list);
      activeAttributesLoadedOkRef.current = true;
    } catch (e) {
      setActiveAttributes([]);
      setActiveAttributesError(e?.message || "Failed to load attributes");
      console.error(e);
    } finally {
      activeAttributesInFlightRef.current = false;
      setActiveAttributesLoading(false);
      setActiveAttributesFetchAttempted(true);
    }
  }, []);

  const submitAttributeAdd = useCallback(async () => {
    if (!canEditPartDetails) return;
    const aid = attributeAddDraft.attributeId.trim();
    if (!aid) {
      setAttributeAddError("Select an attribute.");
      return;
    }
    const pic = part || snapshotRow;
    const partId = buildPartCatalogPartId(pic);
    if (!partId) {
      setAttributeAddError(
        "Cannot build partId (DIV + PLS + part number). Check part data."
      );
      return;
    }
    if (
      Array.isArray(attrsDetailRows) &&
      attrsDetailRows.some(
        (row) => String(row.attributeId ?? "").trim() === aid
      )
    ) {
      setAttributeAddError("This attribute is already on the list.");
      return;
    }
    const selected = activeAttributes.find(
      (r) => String(r.id ?? "").trim() === aid
    );
    const nameDisplay =
      selected && selected.name != null ? String(selected.name).trim() : "";
    const valueStr = String(attributeAddDraft.value ?? "");
    const uomStr = String(attributeAddDraft.uom ?? "");
    const username =
      (typeof localStorage !== "undefined" &&
        localStorage.getItem("username")) ||
      "DBEditorWeb";

    setAttributeAddSubmitting(true);
    setAttributeAddError(null);
    setAttributeDeleteError(null);
    try {
      await addPartCatalogAttribute({
        username,
        partId,
        attributeId: aid,
        value: valueStr,
        uom: uomStr,
      });
      setAttrsDetailRows((prev) => {
        const base = Array.isArray(prev) ? prev : [];
        return [
          ...base,
          {
            attributeId: aid,
            name: nameDisplay,
            value: valueStr,
            uom: uomStr,
          },
        ];
      });
      closeAttributeAddForm();
    } catch (e) {
      console.error(e);
      const data = e?.response?.data;
      const msg =
        (typeof data === "string" && data) ||
        data?.message ||
        e?.message ||
        "Failed to add attribute.";
      setAttributeAddError(msg);
    } finally {
      setAttributeAddSubmitting(false);
    }
  }, [
    canEditPartDetails,
    part,
    snapshotRow,
    attributeAddDraft,
    attrsDetailRows,
    activeAttributes,
    closeAttributeAddForm,
  ]);

  const removeRestrictionRow = useCallback(
    async (index) => {
      if (!canEditPartDetails) return;
      const rows = restsDetailRows;
      if (!Array.isArray(rows) || index < 0 || index >= rows.length) return;
      const row = rows[index];
      const restrictionId = String(row.restrictionId ?? "").trim();
      if (!restrictionId) {
        window.alert("Missing restriction ID.");
        return;
      }
      if (!window.confirm("Delete this restriction from the server?")) return;

      const pic = part || snapshotRow;
      const partId = buildPartCatalogPartId(pic);
      if (!partId) {
        window.alert(
          "Cannot build partId (DIV + PLS + part number). Check part data."
        );
        return;
      }

      const username =
        (typeof localStorage !== "undefined" &&
          localStorage.getItem("username")) ||
        "DBEditorWeb";

      setRestrictionDeleteError(null);
      setRestrictionSuccessMessage(null);
      setRestDeletingRowIndex(index);
      try {
        await deletePartCatalogRestriction({
          username,
          partId,
          restrictionId,
        });
        await refreshRestsDetailRows();
      } catch (e) {
        console.error(e);
        const data = e?.response?.data;
        const msg =
          (typeof data === "string" && data) ||
          data?.message ||
          e?.message ||
          "Failed to delete restriction.";
        setRestrictionDeleteError(msg);
      } finally {
        setRestDeletingRowIndex(null);
      }
    },
    [canEditPartDetails, part, snapshotRow, restsDetailRows, refreshRestsDetailRows]
  );

  const closeRestrictionAddForm = useCallback(() => {
    setRestrictionAddOpen(false);
    setRestrictionAddDraft({ ...EMPTY_RESTRICTION_ADD_DRAFT });
    setRestrictionAddError(null);
  }, []);

  const submitRestrictionAdd = useCallback(async () => {
    if (!canEditPartDetails) return;
    const rid = restrictionAddDraft.restrictionId.trim();
    if (!rid) {
      setRestrictionAddError("Select a restriction.");
      return;
    }
    const flag =
      normalizeRestrictionIncludeExclude(
        restrictionAddDraft.includeExcludeFlag
      ) ?? "I";
    const pic = part || snapshotRow;
    const partId = buildPartCatalogPartId(pic);
    if (!partId) {
      setRestrictionAddError(
        "Cannot build partId (DIV + PLS + part number). Check part data."
      );
      return;
    }
    if (
      Array.isArray(restsDetailRows) &&
      restsDetailRows.some(
        (row) => String(row.restrictionId ?? "").trim() === rid
      )
    ) {
      setRestrictionAddError("This restriction is already on the list.");
      return;
    }
    const username =
      (typeof localStorage !== "undefined" &&
        localStorage.getItem("username")) ||
      "DBEditorWeb";
    setRestrictionAddSubmitting(true);
    setRestrictionAddError(null);
    setRestrictionDeleteError(null);
    setRestrictionSuccessMessage(null);
    try {
      await addPartCatalogRestriction({
        username,
        partId,
        restrictionId: rid,
        includeExcludeFlag: flag,
      });
      await refreshRestsDetailRows();
      closeRestrictionAddForm();
      setRestrictionSuccessMessage("Restriction added successfully.");
    } catch (e) {
      console.error(e);
      const data = e?.response?.data;
      const msg =
        (typeof data === "string" && data) ||
        data?.message ||
        e?.message ||
        "Failed to add restriction.";
      setRestrictionAddError(msg);
    } finally {
      setRestrictionAddSubmitting(false);
    }
  }, [
    canEditPartDetails,
    part,
    snapshotRow,
    restrictionAddDraft,
    restsDetailRows,
    closeRestrictionAddForm,
    activeRestrictions,
    refreshRestsDetailRows,
  ]);

  const toggleRestrictionDivplsIncludeExclude = useCallback(
    async (index, restrictionId) => {
      if (!canEditPartDetails) return;
      const rid = String(restrictionId ?? "").trim();
      if (!rid) return;
      const apiFlag = DIVPLS_UNCHECK_API_INCLUDE_EXCLUDE_FLAG;
      const pic = part || snapshotRow;
      const partId = buildPartCatalogPartId(pic);
      if (!partId) {
        setRestrictionDeleteError(
          "Cannot build partId (DIV + PLS + part number). Check part data."
        );
        return;
      }
      if (divplsIncludeExcludeCommitInFlightRef.current) return;

      const username =
        (typeof localStorage !== "undefined" &&
          localStorage.getItem("username")) ||
        "DBEditorWeb";

      setRestrictionDeleteError(null);
      setRestrictionSuccessMessage(null);
      divplsIncludeExcludeCommitInFlightRef.current = true;
      setDivplsIncludeExcludeSavingIndex(index);
      try {
        await updatePartCatalogRestrictionIncludeExclude({
          username,
          partId,
          restrictionId: rid,
          includeExcludeFlag: apiFlag,
        });
        await refreshRestsDetailRows();
        setRestrictionSuccessMessage("DIVPLS include/exclude cleared.");
      } catch (e) {
        console.error(e);
        const data = e?.response?.data;
        const msg =
          (typeof data === "string" && data) ||
          data?.message ||
          e?.message ||
          "Failed to update DIVPLS include/exclude.";
        setRestrictionDeleteError(msg);
      } finally {
        divplsIncludeExcludeCommitInFlightRef.current = false;
        setDivplsIncludeExcludeSavingIndex(null);
      }
    },
    [canEditPartDetails, part, snapshotRow, refreshRestsDetailRows]
  );

  const handleSubsAccordionToggle = useCallback(async () => {
    if (subsExpanded) {
      setSubsExpanded(false);
      return;
    }
    setSubsExpanded(true);
    if (subsDetailRows !== null || !itemId) return;

    setSubsDetailLoading(true);
    setSubsDetailError(null);
    try {
      const apiRows = await fetchSubstitutePartsByItemId(itemId);
      const rows = apiRows.map((r) => ({
        subItemId: r.subItemId ?? "",
        subPartNumber: r.subPartNumber ?? "",
        subDescription: r.subDescription ?? "",
        subDivision: r.subDivision ?? "",
        subPls: r.subPls ?? "",
        subPartAddDate: r.subPartAddDate ?? "",
        subAvailability:
          r.subAvailability != null && typeof r.subAvailability === "object"
            ? r.subAvailability?.code ?? ""
            : r.subAvailability ?? "",
        subPrice: r.subPrice ?? "",
        subProductGroupDescription: r.subProductGroupDescription ?? "",
        subImageUrl: r.subImageUrl ?? "",
      }));
      setSubsDetailRows(rows);
    } catch (e) {
      console.error(e);
      setSubsDetailRows([]);
      setSubsDetailError(e?.message || "Failed to load substitution parts");
    } finally {
      setSubsDetailLoading(false);
    }
  }, [subsExpanded, subsDetailRows, itemId]);

  if (!open || !snapshotRow) return null;

  const closeLightbox = () => setEnlargedImageUrl(null);

  const titleRow = part || snapshotRow;
  const availNormalized = part ? normalizeAvailabilityShape(part.availability) : { code: "" };
  const availCode =
    availNormalized.code !== "" ? availNormalized.code : "";
  const restrictionCatalogPartId = buildPartCatalogPartId(
    part || snapshotRow
  );
  const restrictionAddControlsDisabled =
    !restrictionCatalogPartId ||
    restsDetailLoading ||
    restrictionAddSubmitting ||
    restDeletingRowIndex !== null ||
    divplsIncludeExcludeSavingIndex !== null ||
    attrValueUomEdit !== null ||
    attrValueUomSaving !== null ||
    attributeAddOpen ||
    attributeAddSubmitting;

  const attributeAddControlsDisabled =
    !restrictionCatalogPartId ||
    attrsDetailLoading ||
    attributeAddSubmitting ||
    attrDeletingRowIndex !== null ||
    attrValueUomSaving !== null ||
    attrValueUomEdit !== null;

  const modal = createPortal(
    <div
      className="pscPartDetailsOverlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={
          canEditPartDetails
            ? "pscPartDetailsDialog"
            : "pscPartDetailsDialog pscPartDetailsReadOnly"
        }
        role="dialog"
        aria-modal="true"
        aria-labelledby="psc-part-details-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pscPartDetailsDialogHeader">
          <h2 id="psc-part-details-title" className="pscPartDetailsDialogTitle">
            Parts Details — Part # {partDetailTitleSuffix(titleRow)}
          </h2>
          <button
            type="button"
            className="pscPartDetailsCloseBtn"
            onClick={onClose}
            aria-label="Close part details"
          >
            <CloseIcon fontSize="medium" />
          </button>
        </div>

        <div className="pscPartDetailsDialogBody">
          {loading && !part ? (
            <div className="partDetailsLoading" aria-live="polite">
              <CircularProgress size={40} thickness={4} />
              <span>Loading part details…</span>
            </div>
          ) : error && !part ? (
            <p className="partDetailsBanner" role="alert">
              {error}
            </p>
          ) : part ? (
            <>
              {loading ? (
                <p className="partDetailsBannerSoft" aria-live="polite">
                  Refreshing details…
                </p>
              ) : null}
              {softWarning ? (
                <p className="partDetailsBannerSoft" role="status">
                  {softWarning}
                </p>
              ) : null}

              <div className="pscPartDetailsTwoCol">
                <div className="pscPartDetailsMainCol">
                  <section className="pscDetailSection" aria-labelledby="psc-part-details-section-title">
                    <h3 id="psc-part-details-section-title" className="pscDetailSectionHeader">
                      Part Details
                    </h3>
                    <div className="pscDetailSectionBody">
              <dl className="partDetailsGrid">
                <dt>Item ID</dt>
                <dd>{cellText(part.itemId)}</dd>
                <dt>DIV</dt>
                <dd>{cellText(part.div)}</dd>
                <dt>PLS</dt>
                <dd>{cellText(part.pls)}</dd>
                <dt>Part Number</dt>
                <dd>{cellText(part.partNumber)}</dd>
                <dt>Description</dt>
                <dd>{cellText(part.description ?? part.partName)}</dd>
                <dt>Rank</dt>
                <dd>{cellText(part.partRank)}</dd>
                <dt>Date Added</dt>
                <dd>{cellText(part.addedDate ?? part.partAddDate)}</dd>
                <dt>Availability</dt>
                <dd>
                  {availCode ? (
                    <span title={availabilityTooltipForCode(availCode)}>
                      {availCode}
                    </span>
                  ) : (
                    "-"
                  )}
                </dd>
                <dt>Price</dt>
                <dd>{formatPartPrice(part.partPrice)}</dd>
                <dt>SON</dt>
                <dd>{cellText(part.son)}</dd>
                <dt>Source Code</dt>
                <dd>{cellText(part.partSourceCode)}</dd>
                <dt>Route Code</dt>
                <dd>{cellText(part.routeCode)}</dd>
                <dt>Image URL</dt>
                <dd>
                  {part.imageUrl != null &&
                  String(part.imageUrl).trim() !== "" ? (
                    <div className="pscPartImageUrlRow">
                      <span className="pscPartImageUrlText">
                        {String(part.imageUrl).trim()}
                      </span>
                      <button
                        type="button"
                        className="pscPartImagePreviewBtn"
                        onClick={() =>
                          setEnlargedImageUrl(String(part.imageUrl).trim())
                        }
                        aria-label="Preview part image"
                      >
                        Preview
                      </button>
                    </div>
                  ) : (
                    "-"
                  )}
                </dd>
                <dt>DIV Description</dt>
                <dd>{cellText(part.divisionDescription)}</dd>
                <dt>Accessory Flag</dt>
                <dd>{formatAccessoryFlag(part.accessoryFlag)}</dd>
                <dt>UPC</dt>
                <dd>{cellText(part.upc)}</dd>
              </dl>
                    </div>
                  </section>
                </div>

                <div className="pscPartDetailsRightStack">
                  <section className="pscDetailSection" aria-labelledby="psc-sub-part-details-title">
                    <h3 id="psc-sub-part-details-title" className="pscDetailSectionHeader">
                      Sub Part Details
                    </h3>
                    <div className="pscDetailSectionBody">
                      {!subsExpanded ? (
                        <button
                          type="button"
                          className="pscSectionExpandPrompt"
                          onClick={handleSubsAccordionToggle}
                        >
                          Click to show substitute parts
                        </button>
                      ) : (
                        <div className="pscSectionPanel">
                          {subsDetailLoading ? (
                            <div className="pscDetailAccordionLoading">
                              <CircularProgress size={28} thickness={4} />
                              <span>Loading substitution parts…</span>
                            </div>
                          ) : subsDetailError ? (
                            <p className="partDetailsBannerSoft" role="status">
                              {subsDetailError}
                            </p>
                          ) : Array.isArray(subsDetailRows) &&
                            subsDetailRows.length === 0 ? (
                            <p className="partDetailsPlain">No substitution parts.</p>
                          ) : (
                            <div className="partDetailsTableWrap">
                              <table className="partDetailsTable">
                                <thead>
                                  <tr>
                                    <th>Item ID</th>
                                    <th>Part number</th>
                                    <th>Description</th>
                                    <th>DIV</th>
                                    <th>PLS</th>
                                    <th>Part add date</th>
                                    <th>Availability</th>
                                    <th>Price</th>
                                    <th>Product group</th>
                                    <th>Image URL</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(subsDetailRows ?? []).map((s, i) => {
                                    const subAvail =
                                      s.subAvailability != null &&
                                      String(s.subAvailability).trim() !== ""
                                        ? String(s.subAvailability).trim()
                                        : "";
                                    const subImg =
                                      s.subImageUrl != null &&
                                      String(s.subImageUrl).trim() !== ""
                                        ? String(s.subImageUrl).trim()
                                        : "";
                                    return (
                                      <tr key={`${s.subItemId ?? ""}-${i}`}>
                                        <td>{cellText(s.subItemId)}</td>
                                        <td>{cellText(s.subPartNumber)}</td>
                                        <td>{cellText(s.subDescription)}</td>
                                        <td>{cellText(s.subDivision)}</td>
                                        <td>{cellText(s.subPls)}</td>
                                        <td>{cellText(s.subPartAddDate)}</td>
                                        <td>
                                          {subAvail ? (
                                            <span
                                              title={availabilityTooltipForCode(
                                                subAvail
                                              )}
                                            >
                                              {subAvail}
                                            </span>
                                          ) : (
                                            "-"
                                          )}
                                        </td>
                                        <td>{cellText(s.subPrice)}</td>
                                        <td>
                                          {cellText(s.subProductGroupDescription)}
                                        </td>
                                        <td className="pscSubImageUrlCell">
                                          {subImg ? (
                                            <div className="pscPartImageUrlRow">
                                              <span className="pscPartImageUrlText">
                                                {subImg}
                                              </span>
                                              <button
                                                type="button"
                                                className="pscPartImagePreviewBtn"
                                                onClick={() =>
                                                  setEnlargedImageUrl(subImg)
                                                }
                                                aria-label="Preview substitution image"
                                              >
                                                Preview
                                              </button>
                                            </div>
                                          ) : (
                                            "-"
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                          <button
                            type="button"
                            className="pscSectionCollapseLink"
                            onClick={handleSubsAccordionToggle}
                          >
                            Hide substitute parts
                          </button>
                        </div>
                      )}
                    </div>
                  </section>

                  <section className="pscDetailSection" aria-labelledby="psc-part-attributes-title">
                    <h3 id="psc-part-attributes-title" className="pscDetailSectionHeader">
                      Part Attributes
                    </h3>
                    <div className="pscDetailSectionBody">
                      {!attrsExpanded ? (
                        <button
                          type="button"
                          className="pscSectionExpandPrompt"
                          onClick={handleAttrsAccordionToggle}
                        >
                          Click to show attributes
                        </button>
                      ) : (
                        <div className="pscSectionPanel">
                          {attributeDeleteError ? (
                            <p className="partDetailsBannerSoft" role="alert">
                              {attributeDeleteError}
                            </p>
                          ) : null}
                          {attributeUpdateSuccessMessage ? (
                            <p
                              className="pscRestrictionUpdateSuccess"
                              role="status"
                              aria-live="polite"
                            >
                              {attributeUpdateSuccessMessage}
                            </p>
                          ) : null}
                          {attrsDetailLoading ? (
                            <div className="pscDetailAccordionLoading">
                              <CircularProgress size={28} thickness={4} />
                              <span>Loading attributes…</span>
                            </div>
                          ) : attrsDetailError ? (
                            <p className="partDetailsBannerSoft" role="status">
                              {attrsDetailError}
                            </p>
                          ) : (
                            <>
                              {canEditPartDetails ? (
                              <div className="pscRestrictionAddToolbar">
                                <div className="pscRestrictionAddToolbarRow">
                                  <button
                                    type="button"
                                    className="pscRestrictionAddBtn"
                                    disabled={attributeAddControlsDisabled}
                                    title={
                                      !restrictionCatalogPartId
                                        ? "Cannot build part ID from part data."
                                        : undefined
                                    }
                                    onClick={() => {
                                      setAttributeAddError(null);
                                      setAttributeUpdateSuccessMessage(null);
                                      if (attributeAddOpen) {
                                        closeAttributeAddForm();
                                      } else {
                                        setAttributeAddDraft({
                                          ...EMPTY_ATTRIBUTE_ADD_DRAFT,
                                        });
                                        setAttributeAddOpen(true);
                                      }
                                    }}
                                  >
                                    {attributeAddOpen
                                      ? "Close add form"
                                      : "Add attribute"}
                                  </button>
                                  {restrictionCatalogPartId ? (
                                    <span
                                      className="pscRestrictionPartIdHint"
                                      title="Used as partId when saving"
                                    >
                                      Part ID:{" "}
                                      <code className="pscRestrictionPartIdCode">
                                        {restrictionCatalogPartId}
                                      </code>
                                    </span>
                                  ) : null}
                                </div>
                                {attributeAddOpen ? (
                                  <div
                                    className="pscRestrictionAddForm"
                                    role="region"
                                    aria-label="Add attribute for this part"
                                  >
                                    <div className="pscRestrictionAddFields">
                                      <div className="pscRestrictionAddField">
                                        <label htmlFor="psc-attribute-add-select">
                                          Attribute
                                        </label>
                                        <select
                                          id="psc-attribute-add-select"
                                          className="pscRestrictionAddSelect"
                                          value={attributeAddDraft.attributeId}
                                          onChange={(e) => {
                                            setAttributeAddError(null);
                                            setAttributeAddDraft((d) => ({
                                              ...d,
                                              attributeId: e.target.value,
                                            }));
                                          }}
                                          onFocus={loadActiveAttributesForAdd}
                                          disabled={
                                            attributeAddSubmitting ||
                                            activeAttributesLoading
                                          }
                                          aria-busy={
                                            activeAttributesLoading
                                              ? "true"
                                              : "false"
                                          }
                                          title={
                                            activeAttributesError || undefined
                                          }
                                        >
                                          <option value="">
                                            {activeAttributesLoading
                                              ? "Loading attributes…"
                                              : activeAttributesError
                                                ? "-- Error — focus to retry --"
                                                : activeAttributesFetchAttempted
                                                  ? "-- Select --"
                                                  : "-- Select — click to load attributes --"}
                                          </option>
                                          {activeAttributes.map((attr) => {
                                            const idStr = String(attr.id ?? "");
                                            const nameStr =
                                              attr.name != null &&
                                              attr.name !== ""
                                                ? String(attr.name)
                                                : "";
                                            const label =
                                              idStr && nameStr
                                                ? `${idStr}, ${nameStr}`
                                                : idStr || nameStr || "—";
                                            return (
                                              <option
                                                key={idStr || label}
                                                value={idStr}
                                              >
                                                {label}
                                              </option>
                                            );
                                          })}
                                        </select>
                                      </div>
                                      <div className="pscRestrictionAddField">
                                        <label htmlFor="psc-attribute-add-value">
                                          Value
                                        </label>
                                        <input
                                          id="psc-attribute-add-value"
                                          type="text"
                                          className="pscRestrictionAddInput"
                                          value={attributeAddDraft.value}
                                          onChange={(e) =>
                                            setAttributeAddDraft((d) => ({
                                              ...d,
                                              value: e.target.value,
                                            }))
                                          }
                                          disabled={attributeAddSubmitting}
                                          autoComplete="off"
                                        />
                                      </div>
                                      <div className="pscRestrictionAddField">
                                        <label htmlFor="psc-attribute-add-uom">
                                          UOM
                                        </label>
                                        <input
                                          id="psc-attribute-add-uom"
                                          type="text"
                                          className="pscRestrictionAddInput"
                                          value={attributeAddDraft.uom}
                                          onChange={(e) =>
                                            setAttributeAddDraft((d) => ({
                                              ...d,
                                              uom: e.target.value,
                                            }))
                                          }
                                          disabled={attributeAddSubmitting}
                                          autoComplete="off"
                                        />
                                      </div>
                                    </div>
                                    {attributeAddError ? (
                                      <p
                                        className="pscRestrictionAddFormError"
                                        role="alert"
                                      >
                                        {attributeAddError}
                                      </p>
                                    ) : null}
                                    <div className="pscRestrictionAddActions">
                                      <button
                                        type="button"
                                        className="pscRestrictionAddSubmitBtn"
                                        disabled={attributeAddSubmitting}
                                        onClick={() => submitAttributeAdd()}
                                      >
                                        {attributeAddSubmitting ? (
                                          <CircularProgress
                                            size={20}
                                            thickness={4}
                                            aria-label="Saving"
                                          />
                                        ) : (
                                          "Save attribute"
                                        )}
                                      </button>
                                      <button
                                        type="button"
                                        className="pscRestrictionAddCancelBtn"
                                        disabled={attributeAddSubmitting}
                                        onClick={closeAttributeAddForm}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                              ) : null}
                              {Array.isArray(attrsDetailRows) &&
                              attrsDetailRows.length === 0 ? (
                                <p className="partDetailsPlain">No attributes.</p>
                              ) : (
                                <div className="partDetailsTableWrap">
                              <table className="partDetailsTable">
                                <thead>
                                  <tr>
                                    <th>Attribute ID</th>
                                    <th>Name</th>
                                    <th>Value</th>
                                    <th>UOM</th>
                                    {canEditPartDetails ? (
                                    <th className="pscTableActionsCol" scope="col">
                                      <span className="pscTableActionsColLabel">
                                        Delete
                                      </span>
                                    </th>
                                    ) : null}
                                  </tr>
                                </thead>
                                <tbody>
                                  {(attrsDetailRows ?? []).map((a, i) => (
                                    <tr key={`${a.attributeId ?? ""}-${i}`}>
                                      <td>{cellText(a.attributeId)}</td>
                                      <td>{cellText(a.name)}</td>
                                      <td className="pscAttrEditableCell">
                                        {canEditPartDetails &&
                                        attrValueUomEdit?.index === i &&
                                        attrValueUomEdit.field === "value" ? (
                                          <div className="pscIncludeExcludeEditWrap">
                                            <input
                                              ref={attrEditInputRef}
                                              type="text"
                                              inputMode="text"
                                              className="pscIncludeExcludeInput"
                                              value={attrValueUomEdit.text}
                                              disabled={
                                                attrValueUomSaving != null
                                              }
                                              aria-busy={
                                                attrValueUomSaving != null
                                                  ? "true"
                                                  : "false"
                                              }
                                              aria-invalid={
                                                attrValueUomError
                                                  ? "true"
                                                  : "false"
                                              }
                                              aria-describedby={
                                                attrValueUomError
                                                  ? `psc-attr-val-err-${i}`
                                                  : undefined
                                              }
                                              onChange={(e) => {
                                                setAttrValueUomError(null);
                                                setAttrValueUomEdit((cur) =>
                                                  cur &&
                                                  cur.index === i &&
                                                  cur.field === "value"
                                                    ? {
                                                        ...cur,
                                                        text: e.target.value,
                                                      }
                                                    : cur
                                                );
                                              }}
                                              onBlur={(e) =>
                                                commitAttrValueUom(
                                                  i,
                                                  "value",
                                                  e.target.value,
                                                  true,
                                                  {
                                                    attributeId: a.attributeId,
                                                    value: a.value,
                                                    uom: a.uom,
                                                  }
                                                )
                                              }
                                              onKeyDown={(e) => {
                                                if (attrValueUomSaving != null)
                                                  return;
                                                if (e.key === "Enter") {
                                                  e.preventDefault();
                                                  commitAttrValueUom(
                                                    i,
                                                    "value",
                                                    e.currentTarget.value,
                                                    false,
                                                    {
                                                      attributeId:
                                                        a.attributeId,
                                                      value: a.value,
                                                      uom: a.uom,
                                                    }
                                                  );
                                                }
                                                if (e.key === "Escape") {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  skipAttrValueUomBlurCommitRef.current = true;
                                                  setAttrValueUomEdit(null);
                                                  setAttrValueUomError(null);
                                                }
                                              }}
                                              aria-label="Attribute value"
                                            />
                                            {attrValueUomSaving?.index === i &&
                                            attrValueUomSaving.field ===
                                              "value" ? (
                                              <p
                                                className="pscIncludeExcludeSavingHint"
                                                aria-live="polite"
                                              >
                                                Saving…
                                              </p>
                                            ) : null}
                                            {attrValueUomError ? (
                                              <p
                                                id={`psc-attr-val-err-${i}`}
                                                className="pscIncludeExcludeFieldError"
                                                role="alert"
                                              >
                                                {attrValueUomError}
                                              </p>
                                            ) : null}
                                          </div>
                                        ) : canEditPartDetails ? (
                                          <button
                                            type="button"
                                            className="pscIncludeExcludeCellBtn"
                                            disabled={
                                              attrDeletingRowIndex !== null ||
                                              attrValueUomSaving !== null ||
                                              restrictionAddOpen ||
                                              attributeAddOpen ||
                                              attributeAddSubmitting ||
                                              (attrValueUomEdit != null &&
                                                (attrValueUomEdit.index !==
                                                  i ||
                                                  attrValueUomEdit.field !==
                                                    "value"))
                                            }
                                            onClick={() => {
                                              setAttributeDeleteError(null);
                                              setAttrValueUomError(null);
                                              setAttrValueUomEdit({
                                                index: i,
                                                field: "value",
                                                text: String(a.value ?? ""),
                                              });
                                            }}
                                            title="Click to edit value"
                                          >
                                            {cellText(a.value)}
                                          </button>
                                        ) : (
                                          cellText(a.value)
                                        )}
                                      </td>
                                      <td className="pscAttrEditableCell">
                                        {canEditPartDetails &&
                                        attrValueUomEdit?.index === i &&
                                        attrValueUomEdit.field === "uom" ? (
                                          <div className="pscIncludeExcludeEditWrap">
                                            <input
                                              ref={attrEditInputRef}
                                              type="text"
                                              inputMode="text"
                                              className="pscIncludeExcludeInput"
                                              value={attrValueUomEdit.text}
                                              disabled={
                                                attrValueUomSaving != null
                                              }
                                              aria-busy={
                                                attrValueUomSaving != null
                                                  ? "true"
                                                  : "false"
                                              }
                                              aria-invalid={
                                                attrValueUomError
                                                  ? "true"
                                                  : "false"
                                              }
                                              aria-describedby={
                                                attrValueUomError
                                                  ? `psc-attr-uom-err-${i}`
                                                  : undefined
                                              }
                                              onChange={(e) => {
                                                setAttrValueUomError(null);
                                                setAttrValueUomEdit((cur) =>
                                                  cur &&
                                                  cur.index === i &&
                                                  cur.field === "uom"
                                                    ? {
                                                        ...cur,
                                                        text: e.target.value,
                                                      }
                                                    : cur
                                                );
                                              }}
                                              onBlur={(e) =>
                                                commitAttrValueUom(
                                                  i,
                                                  "uom",
                                                  e.target.value,
                                                  true,
                                                  {
                                                    attributeId: a.attributeId,
                                                    value: a.value,
                                                    uom: a.uom,
                                                  }
                                                )
                                              }
                                              onKeyDown={(e) => {
                                                if (attrValueUomSaving != null)
                                                  return;
                                                if (e.key === "Enter") {
                                                  e.preventDefault();
                                                  commitAttrValueUom(
                                                    i,
                                                    "uom",
                                                    e.currentTarget.value,
                                                    false,
                                                    {
                                                      attributeId:
                                                        a.attributeId,
                                                      value: a.value,
                                                      uom: a.uom,
                                                    }
                                                  );
                                                }
                                                if (e.key === "Escape") {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  skipAttrValueUomBlurCommitRef.current = true;
                                                  setAttrValueUomEdit(null);
                                                  setAttrValueUomError(null);
                                                }
                                              }}
                                              aria-label="Attribute UOM"
                                            />
                                            {attrValueUomSaving?.index === i &&
                                            attrValueUomSaving.field ===
                                              "uom" ? (
                                              <p
                                                className="pscIncludeExcludeSavingHint"
                                                aria-live="polite"
                                              >
                                                Saving…
                                              </p>
                                            ) : null}
                                            {attrValueUomError ? (
                                              <p
                                                id={`psc-attr-uom-err-${i}`}
                                                className="pscIncludeExcludeFieldError"
                                                role="alert"
                                              >
                                                {attrValueUomError}
                                              </p>
                                            ) : null}
                                          </div>
                                        ) : canEditPartDetails ? (
                                          <button
                                            type="button"
                                            className="pscIncludeExcludeCellBtn"
                                            disabled={
                                              attrDeletingRowIndex !== null ||
                                              attrValueUomSaving !== null ||
                                              restrictionAddOpen ||
                                              attributeAddOpen ||
                                              attributeAddSubmitting ||
                                              (attrValueUomEdit != null &&
                                                (attrValueUomEdit.index !==
                                                  i ||
                                                  attrValueUomEdit.field !==
                                                    "uom"))
                                            }
                                            onClick={() => {
                                              setAttributeDeleteError(null);
                                              setAttrValueUomError(null);
                                              setAttrValueUomEdit({
                                                index: i,
                                                field: "uom",
                                                text: String(a.uom ?? ""),
                                              });
                                            }}
                                            title="Click to edit UOM"
                                          >
                                            {cellText(a.uom)}
                                          </button>
                                        ) : (
                                          cellText(a.uom)
                                        )}
                                      </td>
                                      {canEditPartDetails ? (
                                      <td className="pscTableActionsCell">
                                        {attrDeletingRowIndex === i ? (
                                          <CircularProgress
                                            size={22}
                                            thickness={4}
                                          />
                                        ) : (
                                          <button
                                            type="button"
                                            className="pscRowTrashBtn"
                                            onClick={() => removeAttributeRow(i)}
                                            disabled={
                                              attrDeletingRowIndex !== null ||
                                              restrictionAddOpen ||
                                              attrValueUomSaving !== null ||
                                              attrValueUomEdit != null ||
                                              attributeAddOpen ||
                                              attributeAddSubmitting
                                            }
                                            aria-label={`Delete attribute ${
                                              String(
                                                a.attributeId ?? ""
                                              ).trim() || `row ${i + 1}`
                                            }`}
                                            title="Delete attribute"
                                          >
                                            <DeleteOutlineIcon
                                              fontSize="small"
                                              aria-hidden
                                            />
                                          </button>
                                        )}
                                      </td>
                                      ) : null}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                              )}
                            </>
                          )}
                          <button
                            type="button"
                            className="pscSectionCollapseLink"
                            onClick={handleAttrsAccordionToggle}
                          >
                            Hide attributes
                          </button>
                        </div>
                      )}
                    </div>
                  </section>
                </div>
              </div>

              <section className="pscDetailSection pscDetailSectionFull" aria-labelledby="psc-restrictions-section-title">
                <h3 id="psc-restrictions-section-title" className="pscDetailSectionHeader">
                  Restrictions
                </h3>
                <div className="pscDetailSectionBody">
                  {!restsExpanded ? (
                    <button
                      type="button"
                      className="pscSectionExpandPrompt"
                      onClick={handleRestsAccordionToggle}
                    >
                      Click to show restrictions
                    </button>
                  ) : (
                    <div className="pscSectionPanel">
                      {restrictionDeleteError ? (
                        <p className="partDetailsBannerSoft" role="alert">
                          {restrictionDeleteError}
                        </p>
                      ) : null}
                      {restrictionSuccessMessage ? (
                        <p
                          className="pscRestrictionUpdateSuccess"
                          role="status"
                          aria-live="polite"
                        >
                          {restrictionSuccessMessage}
                        </p>
                      ) : null}
                      {restsDetailLoading ? (
                        <div className="pscDetailAccordionLoading">
                          <CircularProgress size={28} thickness={4} />
                          <span>Loading restrictions…</span>
                        </div>
                      ) : restsDetailError ? (
                        <p className="partDetailsBannerSoft" role="status">
                          {restsDetailError}
                        </p>
                      ) : (
                        <>
                          {canEditPartDetails ? (
                          <div className="pscRestrictionAddToolbar">
                            <div className="pscRestrictionAddToolbarRow">
                              <button
                                type="button"
                                className="pscRestrictionAddBtn"
                                disabled={restrictionAddControlsDisabled}
                                title={
                                  !restrictionCatalogPartId
                                    ? "Cannot build part ID from part data."
                                    : undefined
                                }
                                onClick={() => {
                                  setRestrictionAddError(null);
                                  setRestrictionSuccessMessage(null);
                                  if (restrictionAddOpen) {
                                    closeRestrictionAddForm();
                                  } else {
                                    setRestrictionAddDraft({
                                      ...EMPTY_RESTRICTION_ADD_DRAFT,
                                    });
                                    setRestrictionAddOpen(true);
                                  }
                                }}
                              >
                                {restrictionAddOpen
                                  ? "Close add form"
                                  : "Add restriction"}
                              </button>
                              {restrictionCatalogPartId ? (
                                <span
                                  className="pscRestrictionPartIdHint"
                                  title="Used as partId when saving"
                                >
                                  Part ID:{" "}
                                  <code className="pscRestrictionPartIdCode">
                                    {restrictionCatalogPartId}
                                  </code>
                                </span>
                              ) : null}
                            </div>
                            {restrictionAddOpen ? (
                              <div
                                className="pscRestrictionAddForm"
                                role="region"
                                aria-label="Add restriction for this part"
                              >
                                <div className="pscRestrictionAddFields">
                                  <div className="pscRestrictionAddField">
                                    <label htmlFor="psc-restriction-add-select">
                                      Restriction
                                    </label>
                                    <select
                                      id="psc-restriction-add-select"
                                      className="pscRestrictionAddSelect"
                                      value={restrictionAddDraft.restrictionId}
                                      onChange={(e) => {
                                        setRestrictionAddError(null);
                                        setRestrictionAddDraft((d) => ({
                                          ...d,
                                          restrictionId: e.target.value,
                                        }));
                                      }}
                                      onFocus={loadActiveRestrictionsForAdd}
                                      disabled={
                                        restrictionAddSubmitting ||
                                        activeRestrictionsLoading
                                      }
                                      aria-busy={
                                        activeRestrictionsLoading
                                          ? "true"
                                          : "false"
                                      }
                                      title={activeRestrictionsError || undefined}
                                    >
                                      <option value="">
                                        {activeRestrictionsLoading
                                          ? "Loading restrictions…"
                                          : activeRestrictionsError
                                            ? "-- Error — focus to retry --"
                                            : activeRestrictionsFetchAttempted
                                              ? "-- Select --"
                                              : "-- Select — click to load restrictions --"}
                                      </option>
                                      {activeRestrictions.map((r) => {
                                        const idStr = String(r.id ?? "");
                                        const nameStr =
                                          r.name != null && r.name !== ""
                                            ? String(r.name)
                                            : "";
                                        const label =
                                          idStr && nameStr
                                            ? `${idStr}, ${nameStr}`
                                            : idStr || nameStr || "—";
                                        return (
                                          <option
                                            key={idStr || label}
                                            value={idStr}
                                          >
                                            {label}
                                          </option>
                                        );
                                      })}
                                    </select>
                                  </div>
                                </div>
                                {restrictionAddError ? (
                                  <p
                                    className="pscRestrictionAddFormError"
                                    role="alert"
                                  >
                                    {restrictionAddError}
                                  </p>
                                ) : null}
                                <div className="pscRestrictionAddActions">
                                  <button
                                    type="button"
                                    className="pscRestrictionAddSubmitBtn"
                                    disabled={restrictionAddSubmitting}
                                    onClick={() => submitRestrictionAdd()}
                                  >
                                    {restrictionAddSubmitting ? (
                                      <CircularProgress
                                        size={20}
                                        thickness={4}
                                        aria-label="Saving"
                                      />
                                    ) : (
                                      "Save restriction"
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    className="pscRestrictionAddCancelBtn"
                                    disabled={restrictionAddSubmitting}
                                    onClick={closeRestrictionAddForm}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : null}
                          </div>
                          ) : null}
                          {restsDetailRows !== null &&
                          restsDetailRows.length === 0 ? (
                            <p className="partDetailsPlain" role="status">
                              No restrictions.
                            </p>
                          ) : restsDetailRows !== null &&
                            restsDetailRows.length > 0 ? (
                            <div className="partDetailsTableWrap">
                          <table className="partDetailsTable">
                            <thead>
                              <tr>
                                <th>Restriction ID</th>
                                <th>Restriction name</th>
                                <th>DIVPLS include/exclude</th>
                                {canEditPartDetails ? (
                                <th className="pscTableActionsCol" scope="col">
                                  <span className="pscTableActionsColLabel">
                                    Delete
                                  </span>
                                </th>
                                ) : null}
                              </tr>
                            </thead>
                            <tbody>
                              {(restsDetailRows ?? []).map((r, i) => {
                                const divplsIeKind =
                                  restrictionDivplsIncludeExcludeKind(
                                    r.restrictionIncludeExclude
                                  );
                                const hideRestrictionDelete =
                                  divplsIeKind === "divpls";
                                return (
                                <tr key={`${r.restrictionId ?? ""}-${i}`}>
                                  <td>{cellText(r.restrictionId)}</td>
                                  <td>{cellText(r.restrictionName)}</td>
                                  <td className="pscDivplsIncludeExcludeCell">
                                    <RestrictionDivplsIncludeExcludeCell
                                      value={r.restrictionIncludeExclude}
                                      readOnly={!canEditPartDetails}
                                      disabled={
                                        restDeletingRowIndex !== null ||
                                        restrictionAddOpen ||
                                        attrValueUomEdit !== null ||
                                        attrValueUomSaving !== null ||
                                        attributeAddOpen ||
                                        attributeAddSubmitting ||
                                        (divplsIncludeExcludeSavingIndex !==
                                          null &&
                                          divplsIncludeExcludeSavingIndex !==
                                            i)
                                      }
                                      saving={
                                        divplsIncludeExcludeSavingIndex === i
                                      }
                                      onToggle={() =>
                                        toggleRestrictionDivplsIncludeExclude(
                                          i,
                                          r.restrictionId
                                        )
                                      }
                                    />
                                  </td>
                                  {canEditPartDetails ? (
                                  <td className="pscTableActionsCell">
                                    {hideRestrictionDelete ? null : restDeletingRowIndex ===
                                      i ? (
                                      <CircularProgress size={22} thickness={4} />
                                    ) : (
                                      <button
                                        type="button"
                                        className="pscRowTrashBtn"
                                        onClick={() => removeRestrictionRow(i)}
                                        disabled={restDeletingRowIndex !== null ||
                                          restrictionAddOpen ||
                                          divplsIncludeExcludeSavingIndex !==
                                            null ||
                                          attrValueUomEdit !== null ||
                                          attrValueUomSaving !== null ||
                                          attributeAddOpen ||
                                          attributeAddSubmitting}
                                        aria-label={`Delete restriction ${
                                          String(
                                            r.restrictionId ?? ""
                                          ).trim() || `row ${i + 1}`
                                        }`}
                                        title="Delete restriction"
                                      >
                                        <DeleteOutlineIcon
                                          fontSize="small"
                                          aria-hidden
                                        />
                                      </button>
                                    )}
                                  </td>
                                  ) : null}
                                </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                          ) : null}
                        </>
                      )}
                      <button
                        type="button"
                        className="pscSectionCollapseLink"
                        onClick={handleRestsAccordionToggle}
                      >
                        Hide restrictions
                      </button>
                    </div>
                  )}
                </div>
              </section>
            </>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );

  const lightbox =
    enlargedImageUrl != null
      ? createPortal(
          <div
            className="pscImageLightbox"
            role="dialog"
            aria-modal="true"
            aria-label="Enlarged image"
            onClick={closeLightbox}
          >
            <button
              type="button"
              className="pscImageLightboxClose"
              onClick={closeLightbox}
              aria-label="Close enlarged image"
            >
              <CloseIcon fontSize="medium" />
            </button>
            <img
              src={enlargedImageUrl}
              alt=""
              className="pscImageLightboxImg"
            />
          </div>,
          document.body
        )
      : null;

  return (
    <>
      {modal}
      {lightbox}
    </>
  );
}
